import { useCallback, useEffect, useState } from 'react';

import { shouldSyncPendingOnOpenEdge } from '../filterPanelPlan';

interface UsePendingFilterStateOptions<T extends object> {
  /** Whether the side panel is currently open. */
  open: boolean;
  /**
   * Seed used at mount. Deliberately separate from `buildEmpty`: exploration seeds from an
   * empty panel state, fleet-manager seeds from its already-applied filters. Collapsing the
   * two would change first-mount behaviour for one of them.
   */
  initial: T;
  /** Re-read on the closed→open edge only. A callback, so a fresh object identity per render
   * does not retrigger the sync. */
  getApplied: () => T;
  /** Used by `resetPending` (the panel's "clear all"). */
  buildEmpty: () => T;
}

/**
 * Pending (unsaved) filter-panel state held as ONE object rather than one `useState` per
 * dimension, so adding a filter dimension does not mean touching a parallel list of hooks.
 */
export function usePendingFilterState<T extends object>({
  open,
  initial,
  getApplied,
  buildEmpty,
}: UsePendingFilterStateOptions<T>) {
  const [pending, setPending] = useState<T>(initial);
  const [lastSyncedOpen, setLastSyncedOpen] = useState(open);

  // Effect rather than a render-phase sync, matching the behaviour this replaced: the panel
  // paints once with the previous pending state before the applied filters are re-read.
  useEffect(() => {
    if (shouldSyncPendingOnOpenEdge(open, lastSyncedOpen)) {
      setPending(getApplied());
    }
    setLastSyncedOpen(open);
  }, [open, lastSyncedOpen, getApplied]);

  const setField = useCallback(
    <K extends keyof T>(key: K) =>
      (value: T[K]) => {
        setPending((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
      },
    []
  );

  const resetPending = useCallback(() => {
    setPending(buildEmpty());
  }, [buildEmpty]);

  return { pending, setPending, setField, resetPending };
}
